import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Heart, Users, MessageCircle, Calendar, CheckCircle2, AlertCircle, Lock } from 'lucide-react';

export default function PackDetail() {
  const navigate = useNavigate();
  const { packId } = useParams();
  const [purchasedPacks, setPurchasedPacks] = useState<string[]>([]);
  const [currentDay, setCurrentDay] = useState(1);

  useEffect(() => {
    const stored = localStorage.getItem('tension_purchased_packs');
    if (stored) {
      setPurchasedPacks(JSON.parse(stored));
    }
  }, []);

  const isPurchased = packId ? purchasedPacks.includes(packId) : false;

  // Pack content configurations
  const packContent: any = {
    'breakup-recovery': {
      name: 'Breakup Recovery',
      icon: Heart,
      color: 'from-red-50 to-pink-50',
      borderColor: 'border-red-200',
      iconColor: 'text-red-600',
      duration: 21,
      overview: 'A structured 21-day protocol to process grief, restore self-worth, and make clear-headed decisions about your future.',
      phases: [
        {
          name: 'Days 1-7: Shock & Stabilization',
          description: 'Implement no-contact, manage acute emotional pain, establish safety routines',
          goals: ['Begin no-contact period', 'Process initial grief', 'Build support system'],
        },
        {
          name: 'Days 8-14: Processing & Perspective',
          description: 'Examine relationship patterns, separate facts from fantasy, rebuild identity',
          goals: ['Identify relationship dynamics', 'Challenge idealization', 'Reconnect with self'],
        },
        {
          name: 'Days 15-21: Integration & Direction',
          description: 'Make informed decisions, plan forward movement, establish new patterns',
          goals: ['Decide on contact strategy', 'Set future intentions', 'Celebrate progress'],
        },
      ],
      dailyActions: [
        {
          day: 1,
          title: 'Initiate No-Contact',
          actions: [
            `Delete/archive text thread (don't delete contact - prevents drunk texting)`,
            `Mute/unfollow on social media (don't block - looks reactive)`,
            `Tell 2 trusted people you're doing no-contact for accountability`,
            `Write: "What I'm afraid will happen if I don't reach out"`,
          ],
          insight: `No-contact is not a tactic to make them miss you. It's space for you to think clearly.`,
        },
        {
          day: 2,
          title: 'Feel Everything',
          actions: [
            'Set 2x 15-minute timers to fully feel grief (then stop)',
            'Move your body: 20-minute walk, run, or intense workout',
            `Journal: "The story I'm telling myself about why this happened"`,
            `Call your accountability person if you're tempted to text`,
          ],
          insight: 'Grief comes in waves. Time-boxing emotions prevents spiraling while honoring the pain.',
        },
        {
          day: 3,
          title: 'Reality Check',
          actions: [
            'List 10 things that were actually difficult in the relationship',
            'Write down 3 times you felt anxious, small, or uncertain with them',
            'Notice when you romanticize the past - write down the reality',
            'Avoid social media entirely today',
          ],
          insight: 'Your brain is wired to remember the good. Consciously balance the narrative.',
        },
        // Additional days would continue here...
      ],
      keyPrinciples: [
        `No-contact is for YOUR clarity, not to manipulate their feelings`,
        'Grief is not linear - bad days after good days are normal',
        'Reaching out before 21 days resets your healing timeline',
        'You cannot think your way out of heartbreak - you must feel through it',
      ],
    },
    'reconnection': {
      name: 'Reconnection',
      icon: Users,
      color: 'from-blue-50 to-cyan-50',
      borderColor: 'border-blue-200',
      iconColor: 'text-blue-600',
      duration: 14,
      overview: 'A strategic 14-day framework to rebuild connection after distance, space, or separation without triggering more withdrawal.',
      phases: [
        {
          name: 'Days 1-5: Assessment & First Touch',
          description: 'Evaluate readiness, craft first message, establish light contact',
          goals: ['Determine if timing is right', 'Make calm, confident first move', 'Read initial response'],
        },
        {
          name: 'Days 6-10: Building Bridges',
          description: 'Create positive interactions, avoid pressure, demonstrate change',
          goals: ['Establish consistent but light contact', 'Show growth without announcing it', 'Rebuild safety'],
        },
        {
          name: 'Days 11-14: Deepening & Direction',
          description: 'Introduce deeper conversations, suggest in-person meeting, clarify intentions',
          goals: ['Move beyond surface chat', 'Propose low-pressure meetup', 'Assess mutual interest'],
        },
      ],
      dailyActions: [
        {
          day: 1,
          title: 'Assess Readiness',
          actions: [
            'Ask: Has it been at least 2 weeks since last contact?',
            'Ask: Have I processed my role in the distance?',
            'Ask: Can I handle any response (including none) with dignity?',
            'Write out 3 neutral/positive first message options',
          ],
          insight: 'Reconnection from desperation always fails. Reconnection from stability sometimes works.',
        },
        {
          day: 2,
          title: 'The First Message',
          actions: [
            'Choose a message that\'s specific, positive, and asks nothing of them',
            'Examples: "Saw [thing related to their interest] and thought of you" or "How did [event they mentioned] turn out?"',
            'Send mid-day (not late at night)',
            'Then put phone away for 4 hours minimum',
          ],
          insight: 'The first message should feel like bumping into someone you like, not a relationship negotiation.',
        },
        {
          day: 3,
          title: 'Read the Response',
          actions: [
            'If they responded warmly: Wait 4-6 hours, reply briefly, ask light question',
            'If they responded coldly/briefly: Wait 3 days, try once more or pause',
            'If no response after 48 hours: Accept it and revisit in 2 weeks',
            'Do NOT double-text, explain yourself, or ask why they\'re distant',
          ],
          insight: 'Their response tells you everything. Don\'t negotiate with indifference.',
        },
        {
          day: 5,
          title: 'The Slow Build',
          actions: [
            'If contact is going well: Send 1 message every 2-3 days',
            'Keep it light, positive, and about external topics (not the relationship)',
            'Mirror their effort level - don\'t outpace them',
            'Share something about your life without oversharing',
          ],
          insight: 'Reconnection is a slow burn. Intensity will scare them back into distance.',
        },
        // Additional days...
      ],
      keyPrinciples: [
        'You cannot force someone to re-engage - you can only make it safe for them to choose it',
        'Chasing creates more distance. Calm confidence creates curiosity.',
        'If they wanted to talk, they would. Respect ambivalence as a "no for now"',
        'Reconnection works when both people have grown in the space apart',
      ],
    },
    'conflict-repair': {
      name: 'Conflict Repair',
      icon: MessageCircle,
      color: 'from-yellow-50 to-orange-50',
      borderColor: 'border-yellow-200',
      iconColor: 'text-yellow-600',
      duration: 7,
      overview: 'An intensive 7-day protocol to repair trust, accountability, and emotional safety after arguments or misunderstandings.',
      phases: [
        {
          name: 'Days 1-2: Immediate Repair',
          description: 'De-escalate, take accountability, create safety',
          goals: ['Stop defensive reactions', 'Deliver effective apology', 'Re-establish baseline safety'],
        },
        {
          name: 'Days 3-5: Rebuilding Trust',
          description: 'Follow through on commitments, demonstrate understanding, create new patterns',
          goals: ['Show change through action', 'Validate their experience', 'Rebuild emotional safety'],
        },
        {
          name: 'Days 6-7: Integration',
          description: 'Check for repair completion, establish conflict protocols for future',
          goals: ['Confirm emotional repair', 'Agree on conflict guidelines', 'Return to normalcy'],
        },
      ],
      dailyActions: [
        {
          day: 1,
          title: 'Take Responsibility',
          actions: [
            'Write out: What I did that hurt them (be specific, avoid "but...")',
            'Identify your defensive reaction that escalated things',
            'Craft apology: "I\'m sorry for [specific action]. I understand it made you feel [their feeling]. That wasn\'t okay."',
            'Do NOT: Explain your intentions, justify behavior, or expect immediate forgiveness',
          ],
          insight: 'The first 24 hours determine if this gets better or worse. Accountability > explanations.',
        },
        {
          day: 2,
          title: 'Deliver the Repair',
          actions: [
            'Choose the right time (not when they\'re rushed or still visibly upset)',
            'Deliver apology without defensiveness or counter-complaints',
            'Ask: "What do you need from me to feel safe again?"',
            'Listen without interrupting. Validate their feelings.',
          ],
          insight: 'Repair is not about being understood. It\'s about understanding them.',
        },
        {
          day: 3,
          title: 'Follow Through',
          actions: [
            'Identify 1-2 specific behavior changes you committed to',
            'Demonstrate the change today (don\'t announce it, just do it)',
            'Notice if they\'re testing to see if you meant the apology',
            'Pass the test by staying consistent',
          ],
          insight: 'Apologies are promises. Broken promises after conflict = permanent trust damage.',
        },
        {
          day: 5,
          title: 'Rebuild Lightness',
          actions: [
            'Once intensity has decreased, reintroduce positive interactions',
            'Do something they enjoy together (low stakes)',
            'Avoid heavy conversations today',
            'Laugh together if possible - humor signals safety',
          ],
          insight: 'You can\'t rebuild trust through serious conversations alone. Joy repairs too.',
        },
        // Additional days...
      ],
      keyPrinciples: [
        'Defensiveness during repair makes the original hurt worse',
        'Saying "I\'m sorry you feel that way" is not an apology',
        'Repair requires action, not just words',
        'Some conflicts reveal incompatibility - not all fights should be repaired',
      ],
    },
  };

  const PACK_DISPLAY_NAMES: Record<string, string> = {
    'long-distance': 'Long Distance',
    'anxious-attachment': 'Anxious Attachment',
    'meeting-family': 'Meeting the Family',
    'moving-in': 'Moving In Together',
    'dating-strategy': 'Connection Strategy Pack',
    'profile-optimization': 'Profile Optimization Pack',
    'red-flags-assessment': 'Red Flags Assessment',
    'first-date-guide': 'First Connection Guide',
    'compatibility-deep-dive': 'Alignment Deep Dive',
    'breakup-prevention': 'Early Relationship Success',
    'singles-bundle': 'Complete Singles Bundle',
  };

  function buildGenericPack(id: string) {
    const name =
      PACK_DISPLAY_NAMES[id] ||
      id
        .split('-')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
    return {
      name,
      icon: Heart,
      color: 'from-purple-50 to-pink-50',
      borderColor: 'border-purple-200',
      iconColor: 'text-purple-600',
      duration: 14,
      overview: `Your ${name} program includes structured daily actions, exercises, and guided reflection. More day-by-day content is rolling out — keep your daily rhythm and check the packs hub for updates.`,
      phases: [
        {
          name: 'Week 1: Foundation',
          description: 'Build consistency and awareness.',
          goals: ['Show up daily', 'Track one pattern', 'Note one win per day'],
        },
        {
          name: 'Week 2: Depth',
          description: 'Go deeper on the skills that matter for this program.',
          goals: ['Practice one core skill daily', 'Reflect without judgment', 'Adjust based on what you learn'],
        },
      ],
      dailyActions: [
        {
          day: 1,
          title: 'Start here',
          actions: [
            'Read the program overview',
            'Set a 10-minute daily reminder',
            'Journal in one sentence: what outcome do you want from this pack?',
          ],
          insight: 'Small daily actions compound faster than occasional big efforts.',
        },
      ],
      keyPrinciples: [
        'Progress beats perfection',
        'Consistency creates change',
        'Reach out to support if you feel stuck',
      ],
    };
  }

  const pack = packId ? packContent[packId] || buildGenericPack(packId) : null;

  if (!packId || !pack) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-neutral-600">Pack not found</p>
      </div>
    );
  }

  if (!isPurchased) {
    return (
      <div className="min-h-screen tension-page-bg">
        <div className="bg-white border-b border-neutral-200 px-6 py-4">
          <div className="max-w-3xl mx-auto flex items-center gap-4">
            <button
              onClick={() => navigate('/packs')}
              className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-neutral-600" />
            </button>
            <h1 className="text-xl font-light">{pack.name}</h1>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 py-12">
          <Card className="p-8 text-center space-y-6">
            <Lock className="w-16 h-16 mx-auto text-neutral-400" />
            <div className="space-y-2">
              <h2 className="text-2xl font-light text-neutral-900">Pack Locked</h2>
              <p className="text-neutral-600">
                Purchase the {pack.name} Pack to access this specialized content.
              </p>
            </div>
            <Button onClick={() => navigate('/packs')} size="lg">
              View Pack Details - $2.99
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  const Icon = pack.icon;
  const displayedActions = pack.dailyActions.slice(0, currentDay);

  return (
    <div className="min-h-screen tension-page-bg">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <button
            onClick={() => navigate('/packs')}
            className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-neutral-600" />
          </button>
          <Icon className={`w-6 h-6 ${pack.iconColor}`} />
          <h1 className="text-xl font-light">{pack.name}</h1>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12 space-y-8">
        {/* Overview */}
        <Card className={`p-6 bg-gradient-to-br ${pack.color} ${pack.borderColor}`}>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Calendar className={`w-5 h-5 ${pack.iconColor}`} />
              <h2 className="text-lg font-medium text-neutral-900">{pack.duration}-Day Protocol</h2>
            </div>
            <p className="text-neutral-700">{pack.overview}</p>
          </div>
        </Card>

        {/* Progress Tracker */}
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-neutral-900">Your Progress</h3>
              <span className="text-sm text-neutral-600">Day {currentDay} of {pack.duration}</span>
            </div>
            <div className="w-full bg-neutral-200 rounded-full h-2">
              <div
                className="bg-neutral-900 h-2 rounded-full transition-all"
                style={{ width: `${(currentDay / pack.duration) * 100}%` }}
              />
            </div>
            <div className="flex gap-2">
              {currentDay < pack.duration && (
                <Button
                  size="sm"
                  onClick={() => setCurrentDay(Math.min(pack.duration, currentDay + 1))}
                >
                  Next Day
                </Button>
              )}
              {currentDay > 1 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setCurrentDay(Math.max(1, currentDay - 1))}
                >
                  Previous Day
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Phases */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-neutral-900">Program Phases</h3>
          {pack.phases.map((phase: any, index: number) => (
            <Card key={index} className="p-6">
              <div className="space-y-3">
                <h4 className="font-medium text-neutral-900">{phase.name}</h4>
                <p className="text-sm text-neutral-700">{phase.description}</p>
                <div className="pt-2 space-y-1">
                  {phase.goals.map((goal: string, i: number) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-neutral-600">
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                      <span>{goal}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Daily Actions */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-neutral-900">Daily Action Plan</h3>
          {displayedActions.map((day: any) => (
            <Card
              key={day.day}
              className={`p-6 ${day.day === currentDay ? 'border-2 border-blue-500 shadow-lg' : ''}`}
            >
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-neutral-900">Day {day.day}: {day.title}</h4>
                  {day.day === currentDay && (
                    <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded-full">
                      Current
                    </span>
                  )}
                </div>
                <ul className="space-y-2">
                  {day.actions.map((action: string, i: number) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 mt-0.5 rounded-full border-2 border-neutral-300 flex items-center justify-center flex-shrink-0">
                        <div className="w-2 h-2 rounded-full bg-neutral-300" />
                      </div>
                      <span className="text-sm text-neutral-700">{action}</span>
                    </li>
                  ))}
                </ul>
                <div className={`bg-gradient-to-br ${pack.color} rounded-lg p-4 border ${pack.borderColor}`}>
                  <div className="flex items-start gap-2">
                    <AlertCircle className={`w-4 h-4 ${pack.iconColor} mt-0.5 flex-shrink-0`} />
                    <p className="text-sm text-neutral-700">
                      <strong>Key Insight:</strong> {day.insight}
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Key Principles */}
        <Card className="p-6 bg-neutral-900 text-white">
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Key Principles</h3>
            <ul className="space-y-2 text-sm text-neutral-300">
              {pack.keyPrinciples.map((principle: string, index: number) => (
                <li key={index} className="flex items-start gap-2">
                  <span className="text-neutral-500">•</span>
                  <span>{principle}</span>
                </li>
              ))}
            </ul>
          </div>
        </Card>
      </div>
    </div>
  );
}